import { Controller, useFormContext } from 'react-hook-form';
import { AddressAutocomplete } from '@ab-platform/ui/address-autocomplete';
import { CountryPicker } from '@ab-platform/ui/country-picker';
import { FloatingLabelInput } from '@ab-platform/ui/floating-label-input';
import { FieldError } from '@ab-platform/ui/field-error';
import {
  mapHereAddressToFormAddress,
  type HereAddressFormFields,
} from '@ab-platform/ui/here-maps';
import { env } from '@/lib/env';
import { type RegistrationFormValues } from '@/hooks/useRegistrationForm';

/**
 * Path through `RegistrationFormValues` to a single 5-field address slot.
 * Constrained to the four slots PR-C wires up so a typo in the call site
 * fails at compile time.
 */
type AddressPath =
  | 'addresses.primary'
  | 'addresses.alternateOffice.data'
  | 'addresses.mailingAddress.data'
  | 'addresses.alternateResidence.data';

/**
 * Field path string in dotted notation, used both with `register()` and
 * with `formState.errors` traversal.
 */
type LeafPath = `${AddressPath}.${'street' | 'city' | 'province' | 'zip' | 'country'}`;

interface AddressEntryFieldsProps {
  /** Path under `RegistrationFormValues` to the address object. */
  path: AddressPath;
  /**
   * Header label rendered above the autocomplete (e.g. "Sede legale").
   * The HERE Maps autocomplete keeps its own internal "Cerca indirizzo"
   * label so users always see the same affordance regardless of slot.
   */
  label: string;
}

export function AddressEntryFields({
  path,
  label,
}: AddressEntryFieldsProps): JSX.Element {
  const form = useFormContext<RegistrationFormValues>();
  const { register, getValues, setValue, formState, control } = form;

  // Type-safe error traversal: split the dotted path so we can index the
  // nested error tree. RHF's typed paths give the right narrowing for
  // `register()` but the errors tree is wide and untyped at the leaf —
  // hence the cast to a permissive shape used only for `?.message`.
  const errors = readNested(formState.errors, path) as
    | Record<string, { message?: string } | undefined>
    | undefined;

  return (
    <div className="grid gap-4">
      <div className="text-sm font-medium text-foreground">{label}</div>

      <AddressAutocomplete
        label="Cerca indirizzo"
        apiKey={env.VITE_HERE_API_KEY}
        onSelect={(addr) => {
          const current = getValues(path);
          const adapter: HereAddressFormFields = {
            street: current.street,
            postalCode: current.zip,
            city: current.city,
            province: current.province,
            country: current.country,
          };
          const merged = mapHereAddressToFormAddress(addr, adapter);
          setValue(`${path}.street` as LeafPath, merged.street, {
            shouldValidate: true,
          });
          setValue(`${path}.zip` as LeafPath, merged.postalCode, {
            shouldValidate: true,
          });
          setValue(`${path}.city` as LeafPath, merged.city, {
            shouldValidate: true,
          });
          setValue(`${path}.province` as LeafPath, merged.province, {
            shouldValidate: true,
          });
          setValue(`${path}.country` as LeafPath, merged.country, {
            shouldValidate: true,
          });
        }}
      />

      <FloatingLabelInput
        label="Via e civico"
        autoComplete="street-address"
        error={errors?.street?.message}
        {...register(`${path}.street` as LeafPath)}
      />

      <div className="grid gap-5 sm:grid-cols-2">
        <FloatingLabelInput
          label="Città"
          autoComplete="address-level2"
          error={errors?.city?.message}
          {...register(`${path}.city` as LeafPath)}
        />
        <FloatingLabelInput
          label="Provincia"
          autoComplete="address-level1"
          maxLength={3}
          error={errors?.province?.message}
          // Sigla provincia IT = sempre uppercase ("MI", "RM").
          className="[&_input]:uppercase"
          {...register(`${path}.province` as LeafPath, {
            setValueAs: (v: unknown) =>
              typeof v === 'string' ? v.toUpperCase() : v,
          })}
        />
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <FloatingLabelInput
          label="CAP"
          autoComplete="postal-code"
          inputMode="numeric"
          error={errors?.zip?.message}
          {...register(`${path}.zip` as LeafPath)}
        />
        <div className="grid gap-1.5">
          <Controller
            control={control}
            name={`${path}.country` as LeafPath}
            render={({ field }) => (
              <CountryPicker
                label="Paese"
                value={field.value ?? ''}
                onChange={field.onChange}
              />
            )}
          />
          <FieldError message={errors?.country?.message} />
        </div>
      </div>
    </div>
  );
}

/**
 * Walk a dotted path through an object, returning whatever sits at the
 * leaf. Used here to pluck the per-slot error subtree out of
 * `formState.errors` without hand-typing every conditional.
 */
function readNested(obj: unknown, path: string): unknown {
  let current: unknown = obj;
  for (const key of path.split('.')) {
    if (current === null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}
